import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import {Breadcrumb} from "../../components/ui/Breadcrumb";
import EventCard from "./components/EventCard";
import BalanceCard from "./components/BalanceCard";
import QuickActions from "./components/QuickActions";
import FilterControls from "./components/FilterControls";
import StatsOverview from "./components/StatsOverview";
import Icon from "../../components/AppIcon";
import { Button } from "../../components/ui/Button";
import { apiService } from "./../../services/api";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "../../utils/localization";
import Expense from "../../entities/expense";
import Budget from "../../entities/budget";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { motion } from "framer-motion";

import SpendingOverview from "../../components/dashboard/SpendingOverview";
import CategoryBreakdown from "../../components/dashboard/CategoryBreakdown";
import RecentExpenses from "../../components/dashboard/RecentExpenses";
import BudgetOverview from "../../components/budget/BudgetOverview";

const Dashboard = () => {
  const [events, setEvents] = useState([]);
  const [balances, setBalances] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    status: "all",
    timeRange: "all",
    search: "",
    sortBy: "date-desc",
  });
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  const [error, setError] = useState(null);
  
  // Financial data state
  const [expenses, setExpenses] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [financialLoading, setFinancialLoading] = useState(true);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel
      const [userResponse, statsResponse, eventsResponse, balancesResponse] =
        await Promise.all([
          apiService.getCurrentUser(),
          apiService.getDashboardStats(),
          apiService.getDashboardEvents(),
          apiService.getDashboardBalances(),
        ]);

      setCurrentUser(userResponse.data.user);
      setStats(statsResponse.data.stats);
      setEvents(eventsResponse.data.events);
      setBalances(balancesResponse.data.balances);
    } catch (err) {
      console.error("Failed to load dashboard data:", err);
      setError(err.message);

      // Handle authentication errors
      if (err.message.includes("token") || err.message.includes("auth")) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/expense-loop/login";
      }
    } finally {
      setLoading(false);
    }
  };

  const loadFinancialData = async () => {
    setFinancialLoading(true);
    try {
      const [expenseData, budgetData] = await Promise.all([
        Expense.getAll("-date", 10),
        Budget.getAll("-month"),
      ]);
      setExpenses(expenseData);
      setBudgets(budgetData);
    } catch (error) {
      console.error("Error loading financial dashboard data:", error);
    }
    setFinancialLoading(false);
  };

  useEffect(() => {
    loadDashboardData();
    loadFinancialData();
  }, []);

  const handleRefresh = () => {
    loadDashboardData();
    loadFinancialData();
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleSortChange = (sortBy) => {
    setFilters((prev) => ({ ...prev, sortBy }));
  };

  const filteredAndSortedEvents = React.useMemo(() => {
    let filtered = [...events];

    // Apply status filter
    if (filters?.status !== "all") {
      filtered = filtered?.filter((event) => event?.status === filters?.status);
    }

    // Apply search filter
    if (filters?.search) {
      filtered = filtered?.filter((event) =>
        event?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      );
    }

    // Apply time range filter
    if (filters?.timeRange !== "all") {
      const now = new Date();
      const eventDate = new Date();

      switch (filters?.timeRange) {
        case "week":
          eventDate?.setDate(now?.getDate() - 7);
          break;
        case "month":
          eventDate?.setMonth(now?.getMonth() - 1);
          break;
        case "quarter":
          eventDate?.setMonth(now?.getMonth() - 3);
          break;
        case "year":
          eventDate?.setFullYear(now?.getFullYear() - 1);
          break;
        default:
          break;
      }

      if (filters?.timeRange !== "all") {
        filtered = filtered?.filter(
          (event) => new Date(event.date) >= eventDate
        );
      }
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      switch (filters?.sortBy) {
        case "date-desc":
          return new Date(b.date) - new Date(a.date);
        case "date-asc":
          return new Date(a.date) - new Date(b.date);
        case "amount-desc":
          return b?.totalExpenses - a?.totalExpenses;
        case "amount-asc":
          return a?.totalExpenses - b?.totalExpenses;
        case "name-asc":
          return a?.name?.localeCompare(b?.name);
        case "name-desc":
          return b?.name?.localeCompare(a?.name);
        default:
          return 0;
      }
    });

    return filtered;
  }, [events, filters]);

  // Calculate financial statistics
  const currentMonth = new Date();
  const lastMonth = subMonths(currentMonth, 1);

  const thisMonthExpenses = expenses.filter((expense) => {
    const expenseDate = new Date(expense.date);
    return (
      expenseDate >= startOfMonth(currentMonth) &&
      expenseDate <= endOfMonth(currentMonth)
    );
  });

  const lastMonthExpenses = expenses.filter((expense) => {
    const expenseDate = new Date(expense.date);
    return (
      expenseDate >= startOfMonth(lastMonth) &&
      expenseDate <= endOfMonth(lastMonth)
    );
  });

  const totalThisMonth = thisMonthExpenses.reduce(
    (sum, expense) => sum + expense.amount,
    0
  );
  const totalLastMonth = lastMonthExpenses.reduce(
    (sum, expense) => sum + expense.amount,
    0
  );
  const avgDaily =
    thisMonthExpenses.length > 0 ? totalThisMonth / new Date().getDate() : 0;

  // Calculate current month spending by category for budget tracking
  const currentMonthSpending = {};
  thisMonthExpenses.forEach((expense) => {
    currentMonthSpending[expense.category] =
      (currentMonthSpending[expense.category] || 0) + expense.amount;
  });

  // Get current month budgets
  const currentMonthString = currentMonth.toISOString().slice(0, 7) + "-01";
  const currentMonthBudgets = budgets.filter(
    (budget) => budget.month === currentMonthString
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        {/* <Header user={currentUser} isAuthenticated={true} /> */}
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center min-h-96">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">
                  Loading your dashboard...
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }
  if (error) {
    return (
      <div className="dashboard-error">
        <h3>Unable to load dashboard</h3>
        <p>{error}</p>
        <button onClick={handleRefresh} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Dashboard - ExpenseLoop</title>
        <meta
          name="description"
          content="Manage your shared expenses and track balances with friends and groups."
        />
      </Helmet>
      {/* <Header user={currentUser} isAuthenticated={true} /> */}
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />

          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Welcome back, {currentUser?.name?.split(" ")?.[0]}!
            </h1>
            <p className="text-muted-foreground">
              Here's an overview of your shared expenses and current balances.
            </p>
          </div>

          {/* Stats Overview */}
          <StatsOverview stats={stats} />

          {/* Quick Actions */}
          <QuickActions />

          {/* Financial Overview */}
          <div className="space-y-8 mb-8">
            <SpendingOverview
              totalMonth={totalThisMonth}
              totalLastMonth={totalLastMonth}
              totalExpenses={expenses.length}
              avgDaily={avgDaily}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <CategoryBreakdown expenses={expenses} />
              </div>

              <div className="lg:col-span-1">
                <BudgetOverview
                  budgets={currentMonthBudgets}
                  currentSpending={currentMonthSpending}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <RecentExpenses expenses={expenses} isLoading={financialLoading} />
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Events Section */}
            <div className="lg:col-span-2">
              <FilterControls
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
                totalEvents={events?.length}
                activeFilters={filters}
              />

              {filteredAndSortedEvents?.length === 0 ? (
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                  <Icon
                    name="Calendar"
                    size={48}
                    className="text-muted-foreground mx-auto mb-4"
                  />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    No events found
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {filters?.search ||
                    filters?.status !== "all" ||
                    filters?.timeRange !== "all"
                      ? "Try adjusting your filters to see more events."
                      : "Create your first event to start tracking shared expenses."}
                  </p>
                  <Button
                    variant="default"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => navigate("/create-event")}
                  >
                    Create New Event
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6">
                  {filteredAndSortedEvents?.map((event) => (
                    <EventCard key={event?.id} event={event} />
                  ))}
                </div>
              )}
            </div>

            {/* Balances Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="bg-card border border-border rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-foreground">
                      Balance Summary
                    </h2>
                    <Button variant="ghost" size="sm" iconName="RefreshCw">
                      Refresh
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-3 bg-success/10 rounded-lg">
                      <div className="text-lg font-semibold text-success">
                        {formatCurrency(stats?.totalOwedToYou || 0)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        You're owed
                      </div>
                    </div>
                    <div className="text-center p-3 bg-error/10 rounded-lg">
                      <div className="text-lg font-semibold text-error">
                        {formatCurrency(stats?.totalOwed || 0)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        You owe
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-foreground">
                      Individual Balances
                    </h3>
                    <span className="text-sm text-muted-foreground">
                      {balances?.length} people
                    </span>
                  </div>

                  {balances?.length === 0 ? (
                    <div className="bg-card border border-border rounded-lg p-6 text-center">
                      <Icon
                        name="Users"
                        size={32}
                        className="text-muted-foreground mx-auto mb-2"
                      />
                      <p className="text-sm text-muted-foreground">
                        No balances to show
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {balances?.map((balance, index) => (
                        <BalanceCard key={index} balance={balance} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
